import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { isNil, isEmpty } from 'lodash';
import { CommonModule } from '@angular/common';
import { REMOVED, TestSetupModule } from '@nrwl/test-setup';
import { CommonMaterialModule } from '@nrwl/common-material';
import {
  SchedulerComponent,
  CommonLibModule,
  DevicesService,
  TestMode,
  SchedulerFormat,
  LicensesPageService,
  Device
} from '@nrwl/common-lib';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams
} from '@nrwl/test-setup';
import { FtpConfigFieldMapping, TEST_TYPE, TEST_DURATIONS } from '../../models/models';
import { FtpSetupService } from '../../ftp-setup.service';
import { ScheduleLicense } from '@nrwl/licenses';
@Component({
  selector: 'nrwl-ftp-setup',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './ftp-setup.component.html',
  styleUrls: ['./ftp-setup.component.scss']
})
export class FtpSetupComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();

  @ViewChild('setupForm', { static: true }) form: NgForm;
  @ViewChild(SchedulerComponent) schedulerChild: SchedulerComponent;
  configMapping = FtpConfigFieldMapping;
  tcpPortData: Array<{ name: string; value: number }>;
  newTestMode = true;
  customerName = '';
  technicianName = '';
  contact = '';
  email = '';
  phone = '';
  technicianId = '';
  comments = '';
  testName = 'FTP';
  editMode = false;
  cloneMode = false;
  showDirections = true;
  testInfo: any;
  config: any;
  outputs: Array<string>;
  selectedOriginator: any;
  selectedDestination: any;
  tabsubscription: Subscription;
  agentType: string;
  testMode: string;
  direction: string;
  minBandWidth = 100;
  thresholdMinValue = 1;
  maxBandWidth: number;
  customScheduleEnds: any;
  testTypes = TEST_TYPE;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTCPPortError: any;
  showExternalUrlConfig = true;
  testModeOptions = TestMode;
  formField = {
    testType: null,
    threshold: null,
    tcpPort: null,
    uploadThreshold: null,
    downloadThreshold: null,
    uploadUrl: null,
    downloadUrl: null,
    target: null,
    uploadTarget: null,
    downloadTarget: null
  };
  scheduler: SchedulerFormat;
  scheduleInfoData: SchedulerFormat = {
    repeatInterval: 0,
    timeStamp: '',
    endTime: '',
    isValid: true
  };
  isDisabledScheduler = false;
  schedulerEnds = {
    enabled: false,
    optionsList: TEST_DURATIONS
  };
  readonly unsubscribe: Subject<void> = new Subject();
  ftpScheduleLicense = ScheduleLicense.HTTP;
  isUploadThresholdValid = true;
  isDownloadThresholdValid = true;
  isUploadTargetValid = true;
  isDownloadTargetValid = true;
  constructor(
    private readonly licenseService: LicensesPageService,
    public ftpTestService: FtpSetupService,
    private readonly deviceService: DevicesService
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.ftpScheduleLicense?.includes(license.featureName)
        );
      }
    });
  }

  initializeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.newTestMode = this.ftpTestService.mode === 'new';
    this.editMode = this.ftpTestService.editMode;
    this.cloneMode = this.ftpTestService.mode === 'clone';
    if (!this.newTestMode) {
      const modelAgentType = this.selectedOriginator?.getModel();
      this.restoreAgentBasedConfig(modelAgentType, this.testMode);
      this.restoreFormValues();
    }
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.tab.config;
    this.outputs = this.config.def.outputs;
    this.testName = this.restoreFieldValues(FtpConfigFieldMapping.ALIAS);
    this.customerName = this.restoreFieldValues(FtpConfigFieldMapping.CUSTOMER);
    this.technicianName = this.restoreFieldValues(FtpConfigFieldMapping.TECHINICIAN);
    this.contact = this.restoreFieldValues(FtpConfigFieldMapping.CONTACT);
    this.email = this.restoreFieldValues(FtpConfigFieldMapping.EMAIL);
    this.phone = this.restoreFieldValues(FtpConfigFieldMapping.PHONE);
    this.technicianId = this.restoreFieldValues(FtpConfigFieldMapping.TECHINICIANID);
    this.comments = this.restoreFieldValues(FtpConfigFieldMapping.COMMENTS);
    this.testMode = this.restoreFieldValues(FtpConfigFieldMapping.TEST_MODE);
    this.direction = this.restoreFieldValues(FtpConfigFieldMapping.DIRECTION);
    this.formField.testType = this.restoreFieldValues(FtpConfigFieldMapping.TEST_TYPE);
    this.formField.tcpPort = this.restoreFieldValues(FtpConfigFieldMapping.TCP_PORT);
    this.formField.threshold = this.restoreFieldValues(FtpConfigFieldMapping.THRESHOLD_ENABLED);
    this.formField.uploadThreshold = this.restoreFieldValues(
      FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD
    );
    this.formField.downloadThreshold = this.restoreFieldValues(
      FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD
    );
    this.formField.target = this.restoreFieldValues(FtpConfigFieldMapping.TARGET_ENABLED);
    this.formField.uploadTarget = this.restoreFieldValues(
      FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET
    );
    this.formField.downloadTarget = this.restoreFieldValues(
      FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET
    );
    this.formField.uploadUrl = this.restoreFieldValues(FtpConfigFieldMapping.ENGINE_A_TO_B_URL);
    this.formField.downloadUrl = this.restoreFieldValues(FtpConfigFieldMapping.ENGINE_B_TO_A_URL);
    this.restoreScheduleParams(FtpConfigFieldMapping.SCHEDULER);
    this.selectedOriginator = this.restoreFieldValues(FtpConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(FtpConfigFieldMapping.SELECTED_DESTINATION);
    this.tcpPortData = this.ftpTestService.tcpPorts;
    if (!this.tcpPortData.length) {
      this.tcpPortData = this.ftpTestService.defaultTcpPort;
    }
    if (this.selectedOriginator) {
      this.ftpTestService.setOriginatorAgent(this.selectedOriginator);
      this.maxBandWidth = parseInt(
        this.selectedOriginator?.deviceData?.capabilities?.ftp?.['max-bandwidth']
      );
    }
    if (this.selectedDestination) {
      this.ftpTestService.setDestinationAgent(this.selectedDestination);
    }
    this.getDataBasedOnAgentConfig();
  }

  restoreScheduleParams(field: string): void {
    const data = this.stateHistory(this.componentId, field);
    if (data && data.value !== REMOVED) {
      this.populateScheduler(data.value);
    }
  }

  populateScheduler(schedulerInfo: SchedulerFormat): void {
    this.scheduleInfoData.repeatInterval = schedulerInfo.repeatInterval;
    this.scheduleInfoData.timeStamp = schedulerInfo.timeStamp;
    this.scheduleInfoData.isValid = true;
    this.emitScedulerParams();
  }

  // eslint-disable-next-line
  thresholdToggleChange($event: any): void {
    this.formField.threshold = $event.checked;
    this.onDataEmit(FtpConfigFieldMapping.THRESHOLD_ENABLED, this.formField.threshold, true);
    if (!this.formField.threshold) {
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD,
        this.formField.downloadThreshold,
        true
      );
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD,
        this.formField.uploadThreshold,
        true
      );
    } else {
      this.onDownloadThresholdChange();
      this.onUploadThresholdChange();
    }
  }

  // eslint-disable-next-line
  targetThroughputToggleChange($event: any): void {
    this.formField.target = $event.checked;
    this.onDataEmit(FtpConfigFieldMapping.TARGET_ENABLED, this.formField.target, true);
    if (!this.formField.target) {
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET,
        this.formField.downloadTarget,
        true
      );
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET,
        this.formField.uploadTarget,
        true
      );
    } else {
      this.onDownloadTargetThroughputChange();
      this.onUploadTargetThroughputChange();
    }
  }

  updateTestIdentity(event: {
    testName: string;
    customer: string;
    technician: string;
    contact: string;
    email: string;
    phone: string;
    technicianId: string;
    isValidName: boolean;
    comments: string;
  }): void {
    this.onDataEmit('alias', event.testName, event.isValidName);
    this.onDataEmit('customer', event.customer, true);
    this.onDataEmit('technician', event.technician, true);
    this.onDataEmit('contact', event.contact, true);
    this.onDataEmit('email', event.email, true);
    this.onDataEmit('phone', event.phone, true);
    this.onDataEmit('technicianId', event.technicianId, true);
    this.onDataEmit('comments', event.comments, true);
    const controls = this.form?.form?.controls;
    const allValid = controls ? Object.values(controls).every(control => !control.invalid) : true;
    const isFormValid = event.isValidName && allValid;
    this.form.form?.setErrors(isFormValid ? null : { invalid: true });
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  //eslint-disable-next-line
  onAgentChange(data: any): void {
    if (data['engine-a-info']) {
      data['engine-a-info']['engine-type'] = 'vta';
    }
    if (data['engine-b-info']) {
      data['engine-b-info']['engine-type'] = 'vta';
    }
    this.onDataEmit(FtpConfigFieldMapping.ENGINE_A_INFO, data['engine-a-info'], data.isValid);
    this.onDataEmit(FtpConfigFieldMapping.TEST_MODE, data.testMode, true);
    this.onDataEmit(FtpConfigFieldMapping.DIRECTION, data.direction, true);
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    if (this.selectedOriginator) {
      this.onDataEmit(
        FtpConfigFieldMapping.SELECTED_ORIGINATOR,
        this.selectedOriginator,
        data.isValid
      );
      this.maxBandWidth = parseInt(
        this.selectedOriginator?.deviceData?.capabilities?.ftp?.['max-bandwidth']
      );
      this.agentType = this.selectedOriginator.getModel();
      this.onDataEmit('model', this.agentType, true);
      this.restoreAgentBasedConfig(this.agentType, data.testMode);
      this.restoreFormValues();
    } else if (data.testMode) {
      this.restoreAgentBasedConfig(this.agentType, data.testMode);
      this.restoreFormValues();
    }

    this.selectedDestination = this.deviceService.findDeviceByID(data.destinationId);
    if (this.selectedDestination) {
      this.onDataEmit(
        FtpConfigFieldMapping.SELECTED_DESTINATION,
        this.selectedDestination,
        data.isValid
      );
      this.onDataEmit(FtpConfigFieldMapping.ENGINE_B_INFO, data['engine-b-info'], data.isValid);
      this.ftpTestService.setDestinationAgent(this.selectedDestination);
    }

    if (this.direction === TestMode.UPLOAD) {
      this.onDataEmit(FtpConfigFieldMapping.ENGINE_B_TO_A_URL, this.formField.downloadUrl, true);
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD,
        this.formField.downloadThreshold,
        true
      );
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET,
        this.formField.downloadTarget,
        true
      );
      this.onUploadThresholdChange();
      this.onUploadTargetThroughputChange();
      if (this.testMode === TestMode.MA_TO_EXTERNAL) this.onUploadUrlChange();
    } else if (this.direction === TestMode.DOWNLOAD) {
      this.onDataEmit(FtpConfigFieldMapping.ENGINE_A_TO_B_URL, this.formField.uploadUrl, true);
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD,
        this.formField.uploadThreshold,
        true
      );
      this.onDataEmit(
        FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET,
        this.formField.uploadTarget,
        true
      );
      this.onDownloadThresholdChange();
      this.onDownloadTargetThroughputChange();
      if (this.testMode === TestMode.MA_TO_EXTERNAL) this.onDownloadUrlChange();
    } else {
      if (!this.formField.uploadThreshold) {
        this.formField.uploadThreshold = 100;
      }
      if (!this.formField.downloadThreshold) {
        this.formField.downloadThreshold = 100;
      }
      this.onDownloadThresholdChange();
      this.onUploadThresholdChange();
      if (this.testMode === TestMode.MA_TO_EXTERNAL) {
        this.onUploadUrlChange();
        this.onDownloadUrlChange();
      }
    }
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  isValidThreshold(threshold: number): boolean {
    if (this.maxBandWidth) {
      return threshold >= this.thresholdMinValue && threshold <= this.maxBandWidth;
    }
  }

  onUploadThresholdChange(): void {
    let isValid = this.isValidThreshold(this.formField.uploadThreshold);
    if (isValid) {
      isValid = this.isThresholdAndTargetFieldsValid('uploadThreshold');
    }
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD,
      this.formField.uploadThreshold,
      isValid
    );
    const isUploadTargetValid = this.isThresholdAndTargetFieldsValid('uploadTarget');
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET,
      this.formField.uploadTarget,
      isUploadTargetValid
    );
  }

  onDownloadThresholdChange(): void {
    let isValid = this.isValidThreshold(this.formField.downloadThreshold);
    if (isValid) {
      isValid = this.isThresholdAndTargetFieldsValid('downloadThreshold');
    }
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD,
      this.formField.downloadThreshold,
      isValid
    );
    const isDownloadTargetValid = this.isThresholdAndTargetFieldsValid('downloadTarget');
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET,
      this.formField.downloadTarget,
      isDownloadTargetValid
    );
  }

  isThresholdAndTargetFieldsValid(field: string): boolean {
    switch (field) {
      case 'uploadThreshold': {
        const isValid = this.isValidThreshold(this.formField.uploadThreshold);
        if (
          this.formField?.uploadThreshold > this.formField?.uploadTarget &&
          this.isUploadTargetValid
        ) {
          this.isUploadThresholdValid = false;
        } else {
          this.isUploadThresholdValid = true;
        }
        return this.isUploadThresholdValid && isValid;
      }
      case 'downloadThreshold': {
        const isValid = this.isValidThreshold(this.formField.downloadThreshold);
        if (
          this.formField?.downloadThreshold > this.formField?.downloadTarget &&
          this.isDownloadTargetValid
        ) {
          this.isDownloadThresholdValid = false;
        } else {
          this.isDownloadThresholdValid = true;
        }
        return this.isDownloadThresholdValid && isValid;
      }
      case 'uploadTarget': {
        const isValid = this.isValidTargetThroughput(this.formField.uploadTarget);
        if (
          this.formField?.uploadTarget < this.formField?.uploadThreshold &&
          this.isUploadThresholdValid
        ) {
          this.isUploadTargetValid = false;
        } else {
          this.isUploadTargetValid = true;
        }
        return this.isUploadTargetValid && isValid;
      }
      case 'downloadTarget': {
        const isValid = this.isValidTargetThroughput(this.formField.downloadTarget);
        if (
          this.formField?.downloadTarget < this.formField?.downloadThreshold &&
          this.isDownloadThresholdValid
        ) {
          this.isDownloadTargetValid = false;
        } else {
          this.isDownloadTargetValid = true;
        }
        return this.isDownloadTargetValid && isValid;
      }
      default:
        return true;
    }
  }

  // Prevents user from entering decimal values
  handleDecimal(event: KeyboardEvent): void {
    if (event.key === '.' || event.key === ',' || event.key === 'e') {
      event.preventDefault();
    }
  }

  // Prevents user from pasting decimal values
  handleDecimalPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    if (pasted.includes('.') || pasted.includes(',') || pasted.includes('e')) {
      event.preventDefault();
    }
  }

  // Sets max limit for input fields
  // eslint-disable-next-line
  enforceMaxLength(event: any, maxLength: number): void {
    const input = event.target;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }

  //eslint-disable-next-line
  isValidExternalURL(url: any): boolean {
    const urlRegex =
      /^(https?|ftp):\/\/((([a-zA-Z\d](-?[a-zA-Z\d])*)\.)+[a-zA-Z]{2,}|(\d{1,3}\.){3}\d{1,3}|([a-fA-F\d:]+))(:([1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5]))?(\/[^\s]*)?$/i;
    return urlRegex.test(url);
  }

  isValidTargetThroughput(target: number): boolean {
    if (this.maxBandWidth) {
      return target >= this.minBandWidth && target <= this.maxBandWidth;
    }
  }

  onUploadTargetThroughputChange(): void {
    let isValid = this.isValidTargetThroughput(this.formField.uploadTarget);
    if (isValid) {
      isValid = this.isThresholdAndTargetFieldsValid('uploadTarget');
    }
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET,
      this.formField.uploadTarget,
      isValid
    );
    const isUploadThresholdValid = this.isThresholdAndTargetFieldsValid('uploadThreshold');
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD,
      this.formField.uploadThreshold,
      isUploadThresholdValid
    );
  }

  onDownloadTargetThroughputChange(): void {
    let isValid = this.isValidTargetThroughput(this.formField.downloadTarget);
    if (isValid) {
      isValid = this.isThresholdAndTargetFieldsValid('downloadTarget');
    }
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET,
      this.formField.downloadTarget,
      isValid
    );
    const isDownloadThresholdValid = this.isThresholdAndTargetFieldsValid('downloadThreshold');
    this.onDataEmit(
      FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD,
      this.formField.downloadThreshold,
      isDownloadThresholdValid
    );
  }

  onDownloadUrlChange(): void {
    if (this.direction !== TestMode.UPLOAD && this.formField.downloadUrl) {
      const isValidUrl = this.isValidExternalURL(this.formField.downloadUrl);
      if (!isValidUrl) {
        this.form.controls.downloadUrl?.setErrors({ regex: 'wrong' });
        this.form.controls.downloadUrl?.markAsDirty();
        this.onDataEmit(FtpConfigFieldMapping.ENGINE_B_TO_A_URL, this.formField.downloadUrl, false);
      } else {
        this.onDataEmit(FtpConfigFieldMapping.ENGINE_B_TO_A_URL, this.formField.downloadUrl, true);
      }
    } else {
      this.onDataEmit(FtpConfigFieldMapping.ENGINE_B_TO_A_URL, this.formField.downloadUrl, false);
    }
  }

  onUploadUrlChange(): void {
    if (this.direction !== TestMode.DOWNLOAD && this.formField.uploadUrl) {
      const isValidUrl = this.isValidExternalURL(this.formField.uploadUrl);
      if (!isValidUrl) {
        this.form.controls.uploadUrl?.setErrors({ regex: 'wrong' });
        this.form.controls.uploadUrl?.markAsDirty();
        this.onDataEmit(FtpConfigFieldMapping.ENGINE_A_TO_B_URL, this.formField.uploadUrl, false);
      } else {
        this.onDataEmit(FtpConfigFieldMapping.ENGINE_A_TO_B_URL, this.formField.uploadUrl, true);
      }
    } else {
      this.onDataEmit(FtpConfigFieldMapping.ENGINE_A_TO_B_URL, this.formField.uploadUrl, false);
    }
  }

  fetchSchedulerVal(event: SchedulerFormat): void {
    this.scheduler = event;
    this.scheduleInfoData.repeatInterval = event.repeatInterval;
    this.scheduleInfoData.timeStamp = this.ftpTestService.getScheduleTimeStamp(event.timeStamp);
    this.scheduleInfoData.isValid = true;
    this.emitScedulerParams();
  }

  emitScedulerParams(): void {
    this.onDataEmit('scheduler', this.scheduleInfoData, this.scheduleInfoData.isValid);
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }

  // eslint-disable-next-line
  onTestTypeChange(): void {
    this.onDataEmit(FtpConfigFieldMapping.TEST_TYPE, this.formField.testType, true);
  }

  // eslint-disable-next-line
  onTcpPortChange(): void {
    this.onDataEmit(FtpConfigFieldMapping.TCP_PORT, this.formField.tcpPort, true);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  isValidYangModel(resultSet: any): boolean {
    if (typeof resultSet === 'string') {
      return false;
    } else {
      return true;
    }
  }

  setTcpPorts(selectedDevice: Device): void {
    if (selectedDevice) {
      const portsList = selectedDevice.deviceData.capabilities?.['phy-list']
        ? selectedDevice.deviceData.capabilities?.['http-list'].find(
            item => item['phy-id'] === selectedDevice.deviceData?.phyId
          )['tcp-ports']
        : selectedDevice.deviceData?.capabilities?.http?.['tcp-ports'];

      let tcpPorts: any = portsList?.toString().replace(/[-,]/g, ' ').split(' ');
      if (tcpPorts?.length > 0) {
        if (portsList.indexOf('-') !== -1) {
          tcpPorts = this.ftpTestService.getRange(parseInt(tcpPorts[0]), parseInt(tcpPorts[1]));
        }
        this.ftpTestService.setTCPPorts(tcpPorts);
        this.tcpPortData = this.ftpTestService.tcpPorts;
      }
    } else {
      this.ftpTestService.tcpPorts = [];
    }
  }

  getDataBasedOnAgentConfig(): void {
    const selectedDevice = this.selectedDestination || this.selectedOriginator;
    if (selectedDevice) {
      this.setTcpPorts(selectedDevice);
    }
  }
}
